import { Box, Card, Typography, Skeleton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ReactElement } from 'react'
import { School, Publish, AccessTime, Verified } from '@mui/icons-material'
import { CourseAccreditationResponse, LightweightCourseV5 } from '../types'

type CourseMetricsProps = {
  courses: LightweightCourseV5[]
  isLoading: boolean
  accreditations: CourseAccreditationResponse[]
  accreditationsStatus: string
}

export default function CourseMetrics({
  courses,
  isLoading,
  accreditations,
  accreditationsStatus,
}: CourseMetricsProps): ReactElement {
  let totalCourses = courses.length
  let publishedCourses = courses.filter(
    (c) => c.published === 'published',
  ).length
  let accreditationsError = accreditationsStatus === 'rejected'
  let accreditedCourseIDs = new Set(
    accreditations
      .filter((a) => a.accreditationType === 'accredited')
      .map((a) => a.courseID),
  )
  let accreditedHours = courses
    .filter((c) => accreditedCourseIDs.has(c.id))
    .reduce((sum, c) => sum + (c.length || 0), 0)
  let nonAccreditedHours = courses
    .filter((c) => !accreditedCourseIDs.has(c.id))
    .reduce((sum, c) => sum + (c.length || 0), 0)

  if (isLoading) {
    return (
      <Box className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card
            key={index}
            className="flex flex-col justify-center p-6 text-center border"
            sx={{
              bgcolor: 'background.paper',
              borderColor: 'divider',
            }}
          >
            <Box className="flex items-center justify-center mb-2">
              <Skeleton
                variant="rectangular"
                width={40}
                height={40}
                className="mr-4 rounded-lg"
              />
              <Skeleton variant="text" width={60} height={40} />
            </Box>
            <Skeleton variant="text" width="70%" className="mx-auto" />
          </Card>
        ))}
      </Box>
    )
  }

  return (
    <Box
      className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 md:grid-cols-4"
      role="region"
      aria-label="Course statistics"
    >
      <Card
        className="flex flex-col justify-center p-6 text-center"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
        component="article"
        aria-label="Total courses count"
      >
        <Box className="flex items-center justify-center mb-2">
          <Box
            className="flex items-center justify-center p-2 mr-4 rounded-lg"
            sx={(theme) => ({
              bgcolor: alpha(theme.palette.primary.main, 0.15),
            })}
          >
            <School sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
          </Box>
          <Typography variant="h4" sx={{ color: 'primary.main' }}>
            {totalCourses}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Total Courses
        </Typography>
      </Card>

      <Card
        className="flex flex-col justify-center p-6 text-center"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
        component="article"
        aria-label="Published courses count"
      >
        <Box className="flex items-center justify-center mb-2">
          <Box
            className="flex items-center justify-center p-2 mr-4 rounded-lg"
            sx={(theme) => ({
              bgcolor: alpha(theme.palette.success.main, 0.15),
            })}
          >
            <Publish sx={{ fontSize: '1.5rem', color: 'success.main' }} />
          </Box>
          <Typography variant="h4" sx={{ color: 'success.main' }}>
            {publishedCourses}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Published
        </Typography>
      </Card>

      <Card
        className="flex flex-col justify-center p-6 text-center"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
        component="article"
        aria-label="Total accredited hours"
      >
        <Box className="flex items-center justify-center mb-2">
          <Box
            className="flex items-center justify-center p-2 mr-4 rounded-lg"
            sx={(theme) => ({ bgcolor: alpha(theme.palette.info.main, 0.15) })}
          >
            <Verified sx={{ fontSize: '1.5rem', color: 'info.main' }} />
          </Box>
          <Typography
            variant="h4"
            sx={{ color: accreditationsError ? 'text.disabled' : 'info.main' }}
          >
            {accreditationsError ? '—' : accreditedHours}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Total Accredited Hours
        </Typography>
      </Card>

      <Card
        className="flex flex-col justify-center p-6 text-center"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
        component="article"
        aria-label="Total non-accredited hours"
      >
        <Box className="flex items-center justify-center mb-2">
          <Box
            className="flex items-center justify-center p-2 mr-4 rounded-lg"
            sx={(theme) => ({
              bgcolor: alpha(theme.palette.warning.main, 0.15),
            })}
          >
            <AccessTime sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              color: accreditationsError ? 'text.disabled' : 'warning.main',
            }}
          >
            {accreditationsError ? '—' : nonAccreditedHours}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Total Non-accredited Hours
        </Typography>
      </Card>
    </Box>
  )
}
